/** Public utility exports. Keep implementations in leaf modules for import optimization. */

// ── Retry & Timeout ──
export { withRetry, fetchWithTimeout } from './retry';
export * from './public-feedback';
export * from './compliance-deadlines';
export * from './funding-scope-basis';
export { createSupabaseReadRetryFetch } from './supabase-fetch-retry';
export type { SupabaseReadRetryOptions } from './supabase-fetch-retry';
export * from './regulatory-rates';
export {
  assessRateReadiness,
  buildRateReadinessBanner,
  findRateProjectionMismatches,
  buildAnAccProjectionComparisons,
} from './regulatory-rates/readiness';
export {
  horizonFor,
  addIsoDays,
  nextScheduledDate,
  standardBpaCents,
} from './regulatory-rates/derive-pure';
export {
  residentialFeeRateBundleV3,
  resolveRateBundle,
  windowsForFamily,
} from './regulatory-rates/resolve';
export { validateRegulatoryRateSet } from './regulatory-rates/validate';
export {
  selectAnAccRate,
  resolveAnAccClassCode,
  selectAnAccBct,
  selectOperationalAnAccBct,
  selectVerifiedRate,
  estimateAnAccFunding,
  sumKnownRateCents,
} from './regulatory-rates/anacc-selection';
export type {
  RateProjectionComparison,
  RegulatoryRateBundle,
  RegulatoryRateCatalogue,
  RegulatoryRateFamilyName,
  RegulatoryRateSet,
  RegulatoryRateValidation,
  RegulatoryRateValue,
  RegulatoryRateWindow,
  ResidentialFeeRateBundleV3,
  AnAccFacilityCategory,
  AnAccRateSelection,
  AnAccDailyEligibleCounts,
} from './regulatory-rates/index';
export {
  dexMonthWindow,
  dexMonthlyDue,
  owesMonthlyDexReport,
  chspReportingOccurrenceCandidates,
} from './chsp-dex-cadence';
export * from './legislative';
export * from './mdm';
export * from './ndis';
export * from './compliance-obligations';
export * from './compliance-ia';
export * from './compliance-packs';
export * from './sah-service-list';
export * from './chsp-visit-form-capture';
export * from './service-execution';
export * from './mdm-award-rates';
export * from './frontline-clinical-review';
export * from './frontline-shift-execution';
export * from './frontline-workflows';
export * from './governance-workflow-continuity';
export * from './compliance-deadlines';
export * from './compliance-escalation';
export * from './compliance-guidance/moments';
export * from './complaint-categories';
export * from './financial-workflow-coherence';
export * from './quality-reporting';
export * from './qi-derivation';
export * from './incident-taxonomy';
export * from './audit-readiness';
export * from './audit-program-scope';
export * from './payroll-run-readiness';
export * from './payroll-period-readiness';
export * from './leave-accrual-expectation';
export * from './leave-accrual-period';
export * from './nurses-personal-leave-basis';
export {
  evaluateWorkerXeroCloseout,
  evaluateXeroCloseoutWorkers,
  XERO_CLOSEOUT_BLOCKER_CODES,
} from './payroll/xero-closeout-gate';
export type {
  XeroCloseoutBlocker,
  XeroCloseoutBlockerCode,
  XeroCloseoutWorkerInput,
  XeroCloseoutWorkerResult,
} from './payroll/xero-closeout-gate';
export {
  DEFAULT_TENANT_PAYROLL_EXPORT_POLICY,
  TENANT_SHIFT_LOADING_REPRESENTATIONS,
  inferVehicleKmProjection,
  isPayslipAllowanceWriteMode,
  resolveLiveXeroWriteMode,
  resolveTenantPayrollExportPolicy,
  xeroCloseoutHardBlocksSnapshot,
} from './payroll/tenant-payroll-export-policy';
export type {
  TenantFirstAidExportMode,
  TenantOvertimePolicy,
  TenantPayrollExportPolicy,
  TenantPayrollExportWriteMode,
  TenantSchadsSourceIdentity,
  TenantShiftLoadingRepresentation,
  TenantVehicleKmProjection,
} from './payroll/tenant-payroll-export-policy';
export * from './payroll-readiness-vocabulary-bridge';
export * from './payroll/worked-time-pay-input';
export * from './payroll/daily-overtime-tier-ledger';
export * from './payroll/multi-zone-pay-clock';
export * from './payroll/averaging-cycle';
export * from './payroll/public-holiday-election';
export * from './payroll/aba-generator';
export * from './payroll/staff-matching';
export { jaroWinkler } from './payroll/jaro-winkler';
export {
  buildShiftEngineInput,
  nursesOvertimeExceptionFromFact,
  classificationToSchadsLevel,
  engineEmployeeCategory,
  ordinaryHoursContribution,
  sourceDerivedConditionalPenaltyCode,
} from './payroll/replay-input-resolver';
export {
  NO_RULE_MEAL_BREAK_TRIGGER_MINUTES,
  NO_RULE_ROSTER_DEFAULT_BREAK_MINUTES,
} from './payroll/roster-break-prefill';
export {
  approvedTimesheetCorrectionBreakMinutes,
  canonicalEmployeeCategory,
  DEFAULT_TIMESHEET_MISSING_BREAK_POLICY,
  parseTimesheetMissingBreakPolicy,
  payrollBreakMinutesForEngine,
  resolveBreakTreatment,
  breakSemanticsForRebuild,
  isV13BreakSemantics,
  legacyStoredTimesheetHours,
  DEFAULT_BREAK_SEMANTICS,
  type BreakSemantics,
  resolveLeaveDeductibleBreakMinutes,
  resolveTimesheetActualBreakInput,
  timesheetHoursForBreakEvidence,
  timesheetHoursForBreakEvidenceSql,
  timesheetMissingBreakPolicyApplies,
} from './payroll/break-treatment';
export { payrollHoursForBreakTreatment } from './payroll/correction-break-hours';
export {
  classifyRosterPresumedBreak,
  rosterBreakContextForRow,
  rosterScheduledBreakMinutes,
  originalBreakIsPresumed,
  originalBreakHasRecordedMinutes,
  originalBreakIsMalformed,
  breakEvidenceObject,
  applyRosterPresumedBreakFacts,
  assertRosterPresumedCarryHours,
  ROSTER_PRESUMED_BREAK_POLICY,
  ROSTER_PRESUMED_BREAK_WARNING,
  BREAK_CONTRADICTIONS,
  BREAK_OBSERVATIONS,
  type RosterBreakContext,
  type RosterBreakIdentity,
} from './payroll/roster-presumed-break';
export {
  blockerImpact,
  isBlockerImpactExplicitlyClassified,
  explicitlyClassifiedReadinessCodes,
  sampleIssueForCode,
  BLOCKER_IMPACT_DEFAULT_SAFE,
  DEFAULT_PAY_CONTEXT_ENFORCEMENT_MODE,
  DEFAULT_ROSTER_PAY_CONTEXT_ASSIGNMENT_POLICY,
  parsePayContextEnforcementMode,
  parseRosterPayContextAssignmentPolicy,
  rosterPayContextIdentityOnlyIsActive,
  rowApprovalBlockingIssues,
  rowBlocksApproval,
  blockerResolutionOwner,
  issueResolutionOwner,
  ISSUE_PAY_HOLD_OWNER_PARAMETER,
  payResolutionBlocksCoordinatorApproval,
  rowCoordinatorApprovalBlockingIssues,
  rowBlocksCoordinatorApproval,
  rowOutstandingPayResolutionIssues,
  outstandingPayResolutionCodes,
  isProviderResponsibilityEvidenceCode,
  providerResponsibilityEvidenceCode,
  providerResponsibilityKindFromCode,
  PROVIDER_RESPONSIBILITY_EVIDENCE_PREFIX,
  PROVIDER_RESPONSIBILITY_EVIDENCE_SUFFIX,
} from './payroll/blocker-impact';
export type {
  BlockerImpact,
  BlockerResolutionOwner,
  PayContextEnforcementMode,
  RosterPayContextAssignmentPolicy,
} from './payroll/blocker-impact';
export {
  getBlockerDescriptor,
  describeBlocker,
  codesRequiringDescriptors,
  fixScopeForCode,
  remedyCapabilityForCode,
  BLOCKER_DESCRIPTOR_REGISTRY,
} from './payroll/blocker-descriptor';
export type {
  BlockerDescriptor,
  BlockerFixScope,
  BlockerRemedyCapability,
} from './payroll/blocker-descriptor';
export {
  parseNestedBlockerItems,
  issueFromFields,
  allEmittedReadinessCodes,
  isApprovedLeaveReadinessCode,
  PAYROLL_RUN_READINESS_ISSUE_CODES,
  PERIOD_AGGREGATOR_BLOCKER_CODES,
  KNOWN_PAY_CONTEXT_NESTED_CODES,
  PAY_CONTEXT_RECALCULATION_CODES,
  KNOWN_EXPORT_BLOCKER_CODES,
  PROVIDER_RESPONSIBILITY_EVIDENCE_CODE_PREFIX,
  PROVIDER_RESPONSIBILITY_EVIDENCE_CODE_SUFFIX,
  APPROVED_LEAVE_READINESS_CODE_PREFIX,
} from './payroll/payroll-readiness-issue';
export type {
  PayrollReadinessIssue,
  PayrollReadinessIssueDomain,
  PayrollReadinessRawSeverity,
  NestedBlockerItem,
} from './payroll/payroll-readiness-issue';
export {
  InvalidOrdinaryHoursCapWindowError,
  ordinaryHoursCapAccumulatorKey,
  ordinaryHoursCapWindowBounds,
  resolveOrdinaryHoursCapWindows,
  validateOrdinaryHoursCapWindowDefinitions,
  validateResolvedOrdinaryHoursCapWindows,
} from './payroll/ordinary-hours-cap-windows';
export type {
  ResolverClassification,
  ResolverTimesheet,
  ResolveShiftEngineInputArgs,
} from './payroll/replay-input-resolver';
export type {
  BreakTreatmentFactInput,
  BreakTreatmentKind,
  BreakTreatmentRuleBlock,
  BreakTreatmentSegment,
  BreakTreatmentSnapshotV1,
  BreakTreatmentSourceKind,
  BreakTreatmentStatus,
  ResolveBreakTreatmentInput,
  ResolveTimesheetActualBreakInput,
  TimesheetBreakMinutesProvenance,
  TimesheetMissingBreakPolicyConfig,
  TimesheetMissingBreakPolicyMode,
} from './payroll/break-treatment';
export type {
  OrdinaryHoursCapAnchorKind,
  OrdinaryHoursCapWindowDefinition,
  ResolvedOrdinaryHoursCapWindow,
} from './payroll/ordinary-hours-cap-windows';
export {
  AGED_CARE_DC_L4_EARLIEST_B24_DATE,
  AGED_CARE_DC_L4_SERVICE_COUNT_START,
  NURSES_AIN_LEAVE_SNAPSHOT_DATE,
  RETAINED_RATE_SNAPSHOT_BY_AWARD,
  resolveClassificationHistoricalInputs,
  unresolvedClassificationHistoricalInputs,
} from './payroll/classification-historical-inputs';
export type {
  AgedCareDcL4Path,
  ClassificationHistoricalCode,
  ClassificationHistoricalInput,
  ClassificationHistoricalKind,
  ClassificationHistoricalResult,
  ClassificationHistoricalStatus,
  RetainedRateAwardCode,
} from './payroll/classification-historical-inputs';
export {
  determinationLinesFromPayDocumentLines,
  isFamilyDomesticViolencePayLine,
  itemisedRenderSnapshot,
  kindFromPayDocumentLineType,
  payDocumentIdFromChecksum,
  renderItemisedPayDocument,
  ITEMISED_PAY_DOCUMENT_VERSION,
} from './payroll/itemised-pay-document';
export type {
  ItemisedPayDocument,
  PayDeterminationLine,
  PayDocumentLineKind,
  PayDocumentSourceLine,
} from './payroll/itemised-pay-document';
export {
  anAccFundingOnOrAfter,
  costFundingEventsForYear,
  formatCostFundingCalendarWarnings,
  mostRecentAwrWageDate,
  resolveCostFundingGap,
  resolveCostFundingGaps,
} from './payroll/cost-funding-calendars';
export type {
  CostFundingEvent,
  CostFundingGap,
  CostFundingGapCode,
  CostFundingGapStatus,
  CostFundingKind,
  CostFundingStream,
} from './payroll/cost-funding-calendars';
export {
  isSalaryFlatConfigComplete,
  workerPayrollSetupStatus,
} from './payroll/worker-payroll-setup';
export type {
  PayrollSetupGap,
  PayrollSetupGapCode,
  WorkerPayrollSetupInput,
  WorkerPayrollSetupSalaryFlatConfig,
  WorkerPayrollSetupStatus,
} from './payroll/worker-payroll-setup';
export { assembleWorkerPayrollSetup } from './payroll/worker-payroll-setup-assembler';
export { followClassificationPin, type ClassificationPinRow } from './payroll/classification-pin';
export {
  WORKER_PAY_ROLE_EMPLOYMENT_COMPATIBILITY,
  workerEmploymentMatchesPayRole,
  workerEmploymentNeedsFactualRecovery,
} from './payroll/worker-pay-role-employment';
export { resolveSingleEffectiveDatedRow } from './payroll/effective-dated-history';
export type { EffectiveDatedHistoryRow } from './payroll/effective-dated-history';
export type {
  WorkerPayrollSetupFacts,
  WorkerPayrollSetupVerdict,
  WorkerPayrollSetupVerdictState,
  WorkerPayrollSetupClassificationRow,
  WorkerPayrollSetupPayRoleRow,
  WorkerPayrollSetupProfileRow,
  WorkerPayrollSetupCalcContext,
} from './payroll/worker-payroll-setup-assembler';
export {
  ReplayEngineVersionMismatchError,
  replayPayRunItem,
} from './payroll/reconciliation-replay';
export type {
  ReplayResult,
  ReplayDivergence,
  ReplayPayRunItemArgs,
  ReplayOptions,
} from './payroll/reconciliation-replay';
export {
  EBA_BLOCK_COMPILER_ADAPTER_VERSION,
  EBA_COMPILE_PROFILES,
  compileEbaOverlay,
  rulePackHash,
} from './payroll/eba-block-compiler';
export type {
  CasualPenaltyConvention,
  CompileProfile,
  CompiledAllowanceLine,
  CompiledConditionalPenaltyKind,
  CompiledConditionalPenaltyLine,
  CompiledEbaOverlay,
  CompiledOvertimeRule,
  CompiledPenaltyLine,
  ManualBlockContract,
  PayRuleBlock,
  StructuralBlock,
} from './payroll/eba-block-compiler';
export {
  EBA_REQUIRED_CONDITIONAL_PENALTY_COVERAGE_GAP_REASON,
  findConditionalPenaltyLine,
  localTimeHHMM,
  pushMissingConditionalPenaltyGap,
  timeInWindow,
} from './payroll/eba-conditional-penalties';
export {
  EBA_CERT_REDUCER_ORDINARY_PENALTY_BANDS,
  EBA_CERT_REDUCER_SHIFT_LOADING_BANDS,
  EBA_CERT_REDUCER_VERSION,
  ebaCertReducerBandUncoveredReason,
  isEbaCertReducerBandInScope,
  reduceEbaExpectedPay,
} from './payroll/eba-cert-reducer';
export type {
  CertDayType,
  CertEmployment,
  CertShiftBand,
  CertShiftInput,
  ExpectedPayBreakdown,
} from './payroll/eba-cert-reducer';
export { EBA_CERT_MATRIX_GENERATOR_VERSION, generateCertMatrix } from './payroll/eba-cert-matrix';
export type {
  CertCoverageReport,
  CertScenario,
  GenerateCertMatrixOptions,
} from './payroll/eba-cert-matrix';
export {
  compareOverlayToAnchor,
  EBA_CERT_ANCHOR_COMPARATOR_VERSION,
  EBA_CERT_ANCHOR_NUMERIC_TOLERANCE,
} from './payroll/eba-cert-anchor';
export type {
  AnchorComparisonReport,
  AnchorComparisonValue,
  AnchorKey,
  AnchorRateLine,
  AnchorWithoutOverlay,
  CompareOverlayToAnchorInput,
  EbaRateScheduleAnchorKind,
  EbaRateScheduleAnchorRow,
  EbaRateScheduleVerificationStatus,
  OverlayWithoutAnchor,
} from './payroll/eba-cert-anchor';
export {
  detectEbaCompiledLineDrift,
  detectEbaDollarAllowanceAwardFloorDrift,
  formatEbaAllowanceAwardFloorWarning,
} from './payroll/eba-drift-detector';
export type {
  DollarAllowanceForAwardFloor,
  EbaAllowanceAwardFloorDrift,
  EbaAllowanceAwardFloorResult,
  EbaCompiledLineDriftResult,
} from './payroll/eba-drift-detector';
export {
  ACCEPTED_EBA_CERT_VERSION_TRIPLES,
  CURRENT_EBA_CERT_VERSION_TRIPLE,
  isAcceptedEbaCertVersionTriple,
} from './payroll/eba-cert-accepted-versions';
export type { EbaCertVersionTriple } from './payroll/eba-cert-accepted-versions';
export {
  aggregateDiffsByClass,
  classifyDiffs,
  DEFAULT_DIFF_THRESHOLDS,
} from './payroll/reconciliation-diff';
export type {
  DiffClass,
  DiffInput,
  DiffOutput,
  DiffThresholds,
} from './payroll/reconciliation-diff';
export {
  EMPLOYER_ONCOSTS_CALCULATOR_VERSION,
  calculateEmployerOncosts,
  financialYearLabel,
  isEmployerOncostsState,
} from './payroll/employer-oncosts';
export type {
  EmployerOncostsInput,
  EmployerOncostsSnapshot,
  EmployerOncostsState,
  PayrollTaxSnapshot,
  PayrollTaxThresholdRow,
  WorkersCompRateRow,
  WorkersCompSnapshot,
} from './payroll/employer-oncosts';
export {
  calculateTasWorkersCompensationPeriodPayment,
  calculateTasWorkersCompensationWeeklyPayment,
  TAS_WORKERS_COMPENSATION_AUTHORITIES,
  TAS_WORKERS_COMPENSATION_CALCULATOR_VERSION,
} from './payroll/workers-compensation-pay';
export type {
  TasWorkersCompensationBlockerCode,
  TasWorkersCompensationCapacity,
  TasWorkersCompensationPeriodPaymentResult,
  TasWorkersCompensationPeriodPaymentSnapshot,
  TasWorkersCompensationWeeklyPaymentInput,
  TasWorkersCompensationWeeklyPaymentResult,
  TasWorkersCompensationWeeklyPaymentSnapshot,
} from './payroll/workers-compensation-pay';
export {
  materialiseRecurringAllowances,
  RECURRING_ALLOWANCE_RESOLVER_VERSION,
} from './payroll/recurring-allowance';
export type {
  ExistingAllowanceEmission,
  MaterialiseRecurringAllowanceInput,
  MaterialiseRecurringAllowanceResult,
  RecurringAllowanceAssignment,
  RecurringAllowanceFrequency,
  RecurringAllowanceLine,
  RecurringAllowanceProrationPolicy,
  RecurringAllowanceStatus,
} from './payroll/recurring-allowance';
export {
  AWR_2026_ALLOWANCE_OPERATIVE_ON,
  SCHADS_ALLOWANCE_STANDARD_WEEKLY_RATE,
  AGED_CARE_ALLOWANCE_STANDARD_WEEKLY_RATE,
  agedCareComputedWageRelatedAllowances,
  isAwr2026AllowanceOperative,
  isAwr2026AllowanceOperativeOnDate,
  schadsAllowancesForShift,
  schadsComputedWageRelatedAllowances,
} from './payroll/wage-related-allowances';
export {
  AWARD_TOIL_VERSION,
  agedCareToilAccrual,
  nursesToilAccrual,
  schadsToilAccrual,
} from './payroll/award-toil';
export type { AwardToilAccrual } from './payroll/award-toil';
export {
  AWARD_PART_TIME_OVERTIME_VERSION,
  agedCarePartTimeOvertime,
  nursesPartTimeOvertime,
  schadsPartTimeOvertime,
} from './payroll/award-part-time-overtime';
export type { PartTimeOvertimeSplit } from './payroll/award-part-time-overtime';
export {
  AUTOMATIC_AWARD_TOPUP_VERSION,
  applyAutomaticAwardTopUps,
  precomputeSchadsSleepoverFlankingTopUps,
} from './payroll/automatic-award-topups';
export {
  deriveAwardMinimumEngagement,
  resolveAwardMinimumEngagementFloor,
  AWARD_MINIMUM_ENGAGEMENT_VERSION,
  AWARD_MINIMUM_ENGAGEMENT_COMPONENT_CODE,
} from './payroll/award-minimum-engagement';
export type {
  AutomaticAwardTopUpCode,
  AutomaticAwardTopUpInput,
  AutomaticAwardTopUpSummary,
  SleepoverFlankingTopUpShift,
} from './payroll/automatic-award-topups';
export {
  SAH_WAGE_CAPTURE_DERIVATION_VERSION,
  SAH_WAGE_CAPTURE_IMMUTABLE_STATUSES,
  SAH_WAGE_CAPTURE_MUTABLE_STATUSES,
  SEGMENT_MERGE_TOLERANCE_MS,
  TRAVEL_ADJACENCY_TOLERANCE_MS,
  buildSahWageCaptureDispatchInput,
  buildWorkPeriodId,
  deriveSahWageCaptureDay,
  isSahHomeCareServiceDeliveryCohort,
  catalogueProgramDeclaresHomeCareAward,
  isSahWageCaptureImmutableStatus,
  isSahWageCaptureMutableStatus,
  isTravelSegmentAuthoritative,
  isVisitImmutable,
  resolveBrokenShiftCount,
  resolveMinimumEngagementFloor,
  sahWageCaptureHelperText,
  stableWageCaptureFactsJson,
  topUpsFromDispatchInput,
  travelSegmentFromDuration,
} from './payroll/sah-wage-capture';
export type {
  BrokenShiftSource,
  SahDispatchSegment,
  SahMinEngagementConfig,
  SahTimesheetWageCaptureFacts,
  SahTravelSegment,
  SahVisitSegment,
  SahWageCaptureDayResult,
  SahWageCaptureDispatchInput,
  SahWageCaptureStatus,
  SahWageCaptureWorkPeriodResult,
  TravelCaptureSource,
  TravelPayableBasis,
} from './payroll/sah-wage-capture';
export {
  SETTLEMENT_PAY_INPUT_SCHEMA_VERSION,
  SETTLEMENT_PAY_FACTS_VERSION,
  buildSettlementPayInput,
  deriveSettlementEffectivePayFacts,
  settlementPayInputDigestPayload,
  workIntervalsFromSegments,
} from './payroll/settlement-pay-input';
export {
  classifySettlementWorkLineageKind,
  settlementWorkIntervalsFromSegments,
  settlementWorkLineageIncludesInterval,
} from './payroll/settlement-work-lineage';
export type { SettlementWorkLineageKind } from './payroll/settlement-work-lineage';
export type {
  SettlementEffectivePayFacts,
  SettlementPayInput,
  SettlementTypedEvidence,
  SettlementWorkInterval,
} from './payroll/settlement-pay-input';
export {
  buildSettlementActualShiftPayInput,
  buildSettlementActualShiftPayInputFromOvertimeInput,
  combineShiftPayBreakdowns,
  computeSettlementActualShiftPayBreakdown,
  deriveSettlementWorkSegmentWindows,
  deriveSettlementWorkSegmentWindowsDetailed,
  settlementActualShiftPaySupportsPayInput,
  settlementWorkSegmentDerivationRefusalReason,
  SETTLEMENT_SEGMENTED_PAY_VERSION,
  shiftPayInputBaseFromOvertimeInput,
} from './payroll/settlement-actual-shift-pay';
export type {
  SettlementActualShiftPayInput,
  SettlementWorkSegmentDerivation,
  SettlementWorkSegmentDerivationFailure,
  SettlementWorkSegmentWindow,
} from './payroll/settlement-actual-shift-pay';
export {
  overtimeMealAllowanceCrossings,
  shiftClassificationGeometry,
} from './payroll/shift-classification-geometry';
export type { ShiftClassificationGeometry } from './payroll/shift-classification-geometry';
export {
  PAID_NOT_WORKED_BREAK_ALLOWANCE_VERSION,
  applyPaidNotWorkedBreakAllowance,
} from './payroll/paid-break-allowance';
export type { PaidNotWorkedBreakAllowanceInput } from './payroll/paid-break-allowance';
export * from './service-delivery-coherence';
export * from './multi-service-visit';
export * from './portal-visibility';
export * from './portal-budget-display';
export * from './portal-legal';
export * from './portal-program-copy';
export * from './customer-legal';
export * from './public-legal';
export * from './workflow-story-packs';
export * from './workflow-orchestration';
export * from './notification-policy';
export * from './notification-policy-configuration';
export * from './notification-policy-candidates';
export * from './outbound-communications';
export * from './app-origin';
export * from './award-rate-lookup';
export * from './allowance-code-canonical';
export * from './cert-currency';
export * from './compliance-data-state';
export * from './compliance-evidence-rate';
export * from './compliance-import';
export * from './anomaly-detection';
export * from './instant-micros';
export * from './roster-gate-detection';
export * from './roster-schedule-series';
export * from './roster-gate-telemetry';
export * from './roster-threshold-profiles';
export * from './award-engines';
export * from './termination';
export { correctionLeaveAccrualDelta } from './payroll/pay-identity-correction';
export type { CorrectionAccrualPolicy } from './payroll/pay-identity-correction';
export * from './worker-availability';
export * from './leave-balance-summary';
export * from './leave-balance-display';
export * from './leave-hours-resolution';
export * from './leave-hours-policy';
export * from './leave-entitlement-policy';
export * from './roster-cost-estimate';
export * from './roster-candidate-scoring';
export * from './roster-optimiser';
export * from './roster-chronology';
export * from './roster-generated-note';
export * from './roster-travel-estimate';
export * from './care-minutes';
export * from './rac/settlement-care-minute-output';
export * from './rac/settlement-rn-coverage-output';
export * from './rac/settlement-rac-allocation';
export * from './fin/settlement-billing-quantity';
export * from './fin/private-primary-pricing-contract';
export * from './care-minute-target-resolver';
export * from './roster-shift-labels';
export * from './roster-service-classification';
export * from './roster-classification-grade';
export * from './roster-classification-requirement';
export * from './roster-care-coverage';
export * from './roster-day-role-band-grid';
export * from './roster-unfilled-urgency';
export * from './roster-unfilled-consequence';
export * from './casual-conversion-eligibility';
export * from './eba-scope-resolver';
export * from './e-sign';
export * from './award-engines/hpss-translation';
export * from './reports/framework';
export * from './clinical';
export { serializeReportToCsv } from './reports/csv-serializer';
export type { SerializeOptions } from './reports/csv-serializer';
export * from './reporting';

// ── SCHADS pay calculator (Phase 1.2 wiring) ──
export {
  calculateShiftPay,
  calculateShiftPayWithOvertime,
  validateShiftCompliance,
  validateWeekRoster,
  normalizeSchadsLevel,
  lookupSchadsRate,
  applyWeeklyAllowanceCaps,
} from './schads';
export {
  PR798459_OPERATIVE_ON,
  PR798459_TWELVE_HOUR_ORDINARY_CAP,
  PR798459_MAX_ORDINARY_EITHER_SIDE,
  isPr798459Operative,
  isPr798459OperativeOnDate,
  isSleepoverFlankSameShift,
} from './schads-pr798459';
export {
  SCHADS_REMOTE_WORK_CLAUSE,
  schadsRemoteWorkMinimumHours,
  schadsRemoteWorkPayableHours,
  isSchadsRemoteWork,
} from './schads-remote-work';
export type { SchadsRemoteWorkKind } from './schads-remote-work';
export type {
  ShiftPayInput,
  ShiftPayBreakdown,
  ShiftPayCoverageGap,
  ShiftPayAllowanceLine,
  SchadsRatesOverride,
  RosterShiftForCompliance,
  RosterComplianceIssue,
  NormalizedSchadsLevel,
  SchadsEmploymentType,
  SchadsEmployeeCategory,
  ShiftForCap,
  CappedShiftResult,
} from './schads';

// ── SAH contribution calculator (Phase 1.3 wiring) ──
export {
  calculateSahContribution,
  mapDbTierToCanonical,
  mapCanonicalToDbTier,
  isSahMeansBasisComplete,
  mapDbServiceCategoryToCanonical,
  mapCanonicalToDbServiceCategory,
  getAtHmTier,
  atHmRequiresPriorApproval,
  atHmRequiresPrescription,
  atHmExceedsHomeModificationLifetimeCap,
} from './sah';
export type { SahContributionInput, SahContributionBreakdown, AtHmTier } from './sah';
// Compact contribution *display* labels (mobile + shared; never guess null as 0%)
export { contributionDisplayLabel } from './contribution-label';
export type {
  ContributionDisplayLabelInput,
  ContributionHardshipInput,
  ContributionHardshipKind,
} from './contribution-label';
// Mobile point-of-care money visibility matrix (role × surface)
export {
  resolveMobileMoneyVisibility,
  normalizeMobileMoneyRole,
  budgetPressureFromAmounts,
  stripVisitMoneyFields,
  shapeMobileBudgetSummary,
  stripCarePlanQuarterBudget,
  buildMobileVisitParentSelect,
  MOBILE_SAH_VISIT_DETAIL_PROJECTION_RPC,
  MOBILE_VISIT_SAH_DETAIL_PROJECTION_KEYS,
  MOBILE_VISIT_CLIENT_PROJECTION_KEYS,
  MOBILE_VISIT_FORBIDDEN_MONEY_KEYS,
  MOBILE_BUDGET_FORBIDDEN_FOR_SIGNAL_KEYS,
  MOBILE_VISIT_SERVICES_DELIVERED_COLUMNS,
  MOBILE_VISIT_SERVICES_DELIVERED_MONEY_COLUMNS,
} from './mobile-money-visibility';
export type {
  MobileMoneyVisibility,
  MobileMoneyBudgetMode,
  MobileMoneyContributionMode,
  BudgetPressureSignal,
  MobileBudgetSummaryDto,
} from './mobile-money-visibility';
export {
  POOLED_CARE_MANAGEMENT_FUNDING_CODES,
  SEPARATE_ENVELOPE_FUNDING_CODES,
  SAH_PRIMARY_SUPPLEMENT_TYPES,
  isPooledCareManagementFundingCode,
  isPooledCareManagementService,
  isSeparateEnvelopeFundingCode,
  isSahPrimarySupplementType,
  summarisePooledCareManagementAccount,
} from './sah-provider';
export type {
  SahPooledCareManagementServiceLike,
  SahPooledCareManagementSummary,
  SahPooledCareManagementSummaryInput,
} from './sah-provider';
export * from './sah-schedule-intent';
export { buildSahBudgetScenario } from './sah-budget-scenarios';
export type {
  SahBudgetScenarioInput,
  SahBudgetScenarioLineBucket,
  SahBudgetScenarioLineInput,
  SahBudgetScenarioLineResult,
  SahBudgetScenarioMode,
  SahBudgetScenarioResult,
} from './sah-budget-scenarios';
export {
  SAH_TERMINAL_EXCEPTION_STATUSES,
  buildSahServiceLines,
  sahParticipantSpend,
  sahPooledCareManagementLines,
  sahItemisedServiceLines,
  sahChargesSpendComponent,
} from './sah-spend';
export type {
  SahDeliveryRow,
  SahDetailRow,
  SahContributionRow,
  SahServiceLine,
  SahChargeRow,
} from './sah-spend';
export { buildSahBudgetForecast } from './sah-budget-forecast';
export type {
  SahBudgetForecast,
  SahBudgetForecastConfidence,
  SahBudgetForecastInput,
  SahBudgetForecastStatus,
} from './sah-budget-forecast';
export * from './funder';
export * from './funding-transitions';
export * from './sah-eol-vocabulary';
export * from './client-consent';
export * from './client-funding-context';
export * from './client-program-kind';
export * from './representative';
export * from './sah-consent';
export * from './sah-delivery-ladder';
export * from './sah-delivery-blocker-error';
export * from './sah-visit-clock-out-error';
export * from './care-delivery-poc';
export * from './care-plan-currentness';
export * from './sah-readiness';
export * from './sah-readiness-resolver';
export * from './sah-funding-allocation-m0';
export * from './worker-readiness-ladder';
export { resolveServiceAreaForAddress } from './service-areas';
export type {
  ServiceAreaAddressLike,
  ServiceAreaMatchCandidate,
  ServiceAreaResolution,
} from './service-areas';
export { mpirForDate } from './accommodation/mpir';
export * from './accommodation/daily-amount';
export * from './accommodation/dated-basis';
export * from './accommodation/retention';
export {
  RESIDENTIAL_MPIR_NOT_LOADED_MESSAGE,
  calculateDailyAccommodationPayment,
  calculateAccommodationDapAccrual,
  isAccommodationDapDayChargeable,
  calculateResidentialRefundBreakdown,
  deriveAccommodationAgreementBreakdown,
  residentialRoomPriceMpirPct,
  residentialRefundInterestDays,
  statutoryDapDaily,
} from './residential-accommodation';
export type {
  ResidentialAccommodationPaymentType,
  DailyAccommodationPaymentInput,
  AccommodationAgreementBreakdown,
  AccommodationAgreementBreakdownInput,
  ResidentialRefundBreakdown,
  ResidentialRefundBreakdownInput,
  ResidentialRefundInterestDays,
  ResidentialRefundInterestDaysInput,
} from './residential-accommodation';
export * from './residential-days';
export * from './residential-fees';

// ── Observability ──
// Note: `./sentry` (initSentry, captureException) and `./tracing` (initTracing)
// are intentionally NOT re-exported from this barrel. Both import Node-only
// modules (@sentry/node → diagnostics_channel/worker_threads; @opentelemetry/sdk-node
// → @grpc/grpc-js → net) that break Next.js webpack client-bundle analysis even
// when the calling code is server-only. The web app has its own Sentry setup in
// apps/web/sentry.*.config.ts. NestJS services should import directly from
// '@statura/utils/sentry' or '@statura/utils/tracing'.

// ── Feature Flags ──
export { isFeatureEnabled, getAllFlags, requireFeature } from './feature-flags';
export type { FeatureFlags } from './feature-flags';

// ── Timezone-aware date helpers ──
export {
  DEFAULT_TZ,
  getMonthInTz,
  getYearInTz,
  getDayInTz,
  getHourInTz,
  dateOnlyInTz,
  mondayYmdInTz,
  dayBoundsInTz,
  dayLengthSecondsInTz,
  quarterBoundsInTz,
  datetimeLocalToUtcIso,
  resolveLocalDateTime,
  localDateTimeToUtcIso,
} from './timezone';
export type { LocalTimeCandidate, LocalTimeResolution, LocalTimeOptions } from './timezone';
export {
  isRegisteredNurseIdentity,
  normaliseRnIdentityToken,
  RN_IDENTITY_EXACT_TOKENS,
  RN_IDENTITY_PREFIXES,
} from './rn-identity';
export {
  STATUTORY_SCREENING_FLOOR_TYPES,
  DIRECT_CARE_STAFF_ROLES,
  DIRECT_CARE_SERVICE_TYPE_TOKENS,
  DIRECT_CARE_SERVICE_CODE_TOKENS,
  isDirectCareAssignment,
  isStatutoryScreeningFloorType,
  evaluateStatutoryScreeningFloor,
} from './statutory-screening-floor';
export type {
  StatutoryScreeningFloorType,
  StatutoryScreeningFloorResult,
  StatutoryFloorScreeningRecord,
  DirectCareStaffRole,
} from './statutory-screening-floor';
export {
  classifyCredentialRequirement,
  SCREENING_REQUIREMENT_ABSENT_LABEL,
  CERTIFICATION_REQUIREMENT_ABSENT_LABEL,
} from './roster-credential-requirement';
export type {
  CredentialRequirementState,
  CredentialRequirementAssessment,
} from './roster-credential-requirement';
export * from './residential/supplement-eligibility';
export * from './star-ratings';
export * from './medication-schedule';
export * from './medication-calendar';
export * from './medication-occurrences';
export * from './leave-pay-treatment';
export * from './medea-leave-lane-e-policy';
export * from './leave-type-registry';
export * from './leave-type-policy-readiness';
export * from './leave-self-service-types';
export * from './assessment-reassessment-domain';
export * from './assessment-scope';
export * from './document-vocabulary';
export * from './roster-gate-registry';
export * from './roster-gate-policy';
export * from './roster-roll-forward';
export * from './compliance-calendar';
export * from './compliance-settings';
export * from './compliance-screening/attestation';

export {
  applyWeeklyAllowanceMinimums,
  weeklyAllowanceWeekKey,
} from './payroll/weekly-allowance-minimums';
export * from './compliance-determination';
export * from './compliance-whs-notifiability';

export * from './roster-offer-broadcast';

export {
  formatAustralianDate,
  formatAustralianDateTime,
  getFinancialYear,
  getQuarter,
  formatCurrency,
  formatCurrencyCompact,
  AN_ACC_CURRENT_RATE_PERIOD_ID,
  AN_ACC_RATE_PERIODS,
  anAccRatePeriodIdForDate,
  anAccRatesForDate,
  calculateAnAccDailyFunding,
  getFullName,
  getInitials,
  getDisplayName,
  formatMacId,
  validateMacId,
  formatIhi,
  SIRS_REPORTABLE_TYPES,
  SIRS_ACT_PARAGRAPH,
  sirsActCitation,
  SIRS_DEPRECATED_TYPES,
  evaluateSirsPriorityFacts,
  determineSirsPriority,
  isSirsReportable,
  SIRS_FINAL_REPORT_DAYS,
  calculateSirsFinalReportDeadline,
  calculateSirsDeadline,
  todaySydney,
  calculateCarryOver,
  calculateHcpLegacyCarryForward,
  calculateCareManagementBudget,
  calculatePagination,
  generateInvoiceNumber,
  generateClaimReference,
  scoreMmse,
  scoreRudas,
  scoreMoca,
  scoreBraden,
  scoreAdl,
  scoreIadl,
  scoreFallsRisk,
  scoreMna,
  scoreAbbeyPain,
} from './legacy-core';
export type {
  AnAccRatePeriodId,
  AnAccDailyFunding,
  SirsTriageFact,
  SirsTriageInput,
} from './legacy-core';

export * from './sah-fee-estimate';
export * from './sah-budget-calculator';

export { roundSahParticipantContribution } from './sah-contribution-rounding';
export { hasGeneralStaffDailyLoadedOvertime } from './payroll/eba-conditional-penalties';

export type { ShiftPayWithOvertimeInput } from './payroll/daily-overtime-input';
export * from './leave-correction';

export * from './employee-leave-setup';

export * from './leave-correction-proposals';

export * from './leave-shiftworker-history';
export * from './casual-rate';
export {
  backOutRoundingUnits,
  roundDecimalHalfUp,
  roundMoney,
  moneyToCents,
  sumMoney,
} from './money-rounding';

export * from './leave-nurses-monthly-entitlement';

export * from './care-minute-grading';

export * from './residential/pension-deduction-split';
export { complianceEventFanout } from './compliance-event-fanout';

export {
  rebuildDecimal,
  rebuildRateProduct,
  rebuildUnits,
  rebuildLineCents,
  rebuildSalaryCents,
  rebuildCentsRate,
  rebuildLoadingShortfall,
  rebuildLoadingShortfallCents,
} from './payroll/rebuild-pricing';
export type { RebuildDecimal } from './payroll/rebuild-pricing';
export { decimalTicks, ledgerWireCents } from './payroll/ledger-wire-arithmetic';

export {
  enginePricingEvidence,
  enginePricingFactor,
  enginePricingCents,
} from './payroll/engine-pricing-evidence';
export type { EnginePricingEvidence } from './payroll/engine-pricing-evidence';

export {
  compareAnnualLeaveLoadingPeriod,
  type LeaveLoadingPeriodPart,
} from './payroll/annual-leave-loading-period';
export * from './medication-incident-context';

export * from './prevention-review';
export * from './mealtime-instruction';
export * from './qi-programme';
// Preserve the existing root QiExclusion contract; the ledger reason has its own root name.
export type { QiExclusion } from './qi-derivation';
export type { QiExclusion as QiPrimaryExclusion } from './qi-programme';

export { parseOptionalClinicalMeasurement } from './clinical-measurement';
export {
  readInitialWoundMeasurementEvidence,
  initialWoundMeasurementLabel,
  type InitialWoundMeasurementEvidence,
  validateWoundMeasurementEvidence,
  WoundMeasurementValidationError,
  readWoundMeasurementEvidence,
  woundMeasurementStateLabel,
  woundMeasurementsNeedReview,
  type WoundMeasurementEvidence,
  type WoundMeasurementState,
} from './wound-measurement-state';

export { assumeGeneralStaffPriorOrdinaryHours } from './payroll/general-staff-prior-ordinary';
export type { GeneralStaffPriorHoursRules } from './payroll/general-staff-prior-ordinary';
